import { refractor } from 'refractor';
import bash from 'refractor/lang/bash';
import c from 'refractor/lang/c';
import clike from 'refractor/lang/clike';
import cpp from 'refractor/lang/cpp';
import csharp from 'refractor/lang/csharp';
import css from 'refractor/lang/css';
import go from 'refractor/lang/go';
import graphql from 'refractor/lang/graphql';
import java from 'refractor/lang/java';
import javascript from 'refractor/lang/javascript';
import json from 'refractor/lang/json';
import jsx from 'refractor/lang/jsx';
import kotlin from 'refractor/lang/kotlin';
import markup from 'refractor/lang/markup';
import php from 'refractor/lang/php';
import python from 'refractor/lang/python';
import ruby from 'refractor/lang/ruby';
import rust from 'refractor/lang/rust';
import sql from 'refractor/lang/sql';
import swift from 'refractor/lang/swift';
import tsx from 'refractor/lang/tsx';
import typescript from 'refractor/lang/typescript';
import yaml from 'refractor/lang/yaml';

// Register a curated set of common languages once at module load.
[
  bash,
  c,
  clike,
  cpp,
  csharp,
  css,
  go,
  graphql,
  java,
  javascript,
  json,
  jsx,
  kotlin,
  markup,
  php,
  python,
  ruby,
  rust,
  sql,
  swift,
  typescript,
  tsx,
  yaml,
].forEach((lang) => refractor.register(lang));

// Common aliases the model/markdown may emit → registered refractor names.
const LANGUAGE_ALIASES: Record<string, string> = {
  js: 'javascript',
  ts: 'typescript',
  py: 'python',
  rb: 'ruby',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  yml: 'yaml',
  html: 'markup',
  xml: 'markup',
  'c++': 'cpp',
  cs: 'csharp',
  golang: 'go',
  kt: 'kotlin',
};

type HastNode =
  | { type: 'text'; value: string }
  | {
      type: 'element';
      tagName: string;
      properties?: { className?: string[] };
      children: HastNode[];
    };

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const hastToHtml = (nodes: HastNode[]): string =>
  nodes
    .map((node) => {
      if (node.type === 'text') {
        return escapeHtml(node.value);
      }
      const className = (node.properties?.className || []).join(' ');
      return `<span class="${className}">${hastToHtml(node.children)}</span>`;
    })
    .join('');

const resolveLanguage = (codeEl: HTMLElement): string | null => {
  const fromClass = Array.from(codeEl.classList).find((cls) => cls.startsWith('language-'));
  let lang = fromClass ? fromClass.replace('language-', '') : '';

  // Live (editor) markup keeps the language on the enclosing `.code-block`.
  if (!lang) {
    const block = codeEl.closest('.code-block') as HTMLElement | null;
    lang = block?.dataset.language || '';
  }

  lang = lang.trim().toLowerCase();
  lang = LANGUAGE_ALIASES[lang] || lang;

  if (!lang || lang === 'none' || !refractor.registered(lang)) {
    return null;
  }
  return lang;
};

/**
 * Adds Prism token spans to every `<pre><code>` block inside `root`, in place.
 * The colours are supplied by the global `prism.css` theme. Idempotent within a
 * render (guarded by `data-coach-highlighted`); React resets the markup whenever
 * the streamed answer grows, after which this runs again on the new content.
 */
export const highlightCodeBlocks = (root: HTMLElement): void => {
  const codeBlocks = root.querySelectorAll<HTMLElement>('pre code');

  codeBlocks.forEach((codeEl) => {
    if (codeEl.dataset.coachHighlighted === 'true') {
      return;
    }
    const language = resolveLanguage(codeEl);
    if (!language) {
      return;
    }
    try {
      const tree = refractor.highlight(codeEl.textContent || '', language);
      codeEl.innerHTML = hastToHtml(tree.children as HastNode[]);
      codeEl.dataset.coachHighlighted = 'true';
      // Ensure the base theme colour/background applies via `code[class*="language-"]`.
      if (!Array.from(codeEl.classList).some((cls) => cls.startsWith('language-'))) {
        codeEl.classList.add(`language-${language}`);
      }
    } catch {
      // Leave the block as plain text if highlighting fails.
    }
  });
};
